/**
 * Agent Profiles — v2.0.0 (Hermes-style).
 *
 * PR #A: foundation types only. Skills / memory / sessions are stored but
 * their CRUD lands in PR #D / #E. Module invocation lands in PR #B / #C.
 */
export const AGENT_PROFILE_KINDS = ['system', 'user'];
export const AGENT_PROFILE_MODULE_KEYS = [
    'seo',
    // v2.0.0 PR #B — second profile for the SEO module, owns the playbook
    // generation prompt + (likely future) higher-temperature settings,
    // independent of the scoring profile's edits.
    'seo_playbook',
    'hr',
    'scraper',
    'content_calendar',
    'visuals',
    'template_fill',
    'video_editor',
    'support',
    // v2.3 CRM Command Center — analyses call transcripts (tone + profile) and
    // manages CRM scheduling.
    'crm_analyst',
    // v2.8 CRM sales pipeline — scores a lead's win-probability + insights, and
    // ranks leads by priority. One profile, two turn-selected jobs.
    'crm_lead_analyst',
    // v2.8 Sales Mail — drafts a professional email to a lead, mixing the lead's
    // profile with a sales knowledge base. Human-approved before sending.
    'sales_mail',
    // v2.4.0 Odoo ERP MCP integration — the single Odoo AI Agent. Whitelists the
    // 7 odoo_* MCP tools (PR 3); its 1:1 AgentProfileOdooConfig extension carries
    // the confirm-before-write/unlink toggles + export formats (PR 4).
    'odoo',
    // Automation module — one system profile per automation, keyed
    // `automation_<key>`. These are managed from the Automation admin tab and
    // hidden from the Settings → Agent Profiles list (moduleKey prefix filter).
    'automation_marketing',
    // UST-3132 — Telephony Support. Answers the questions an inbound voice AI
    // asks us mid-call (ticket status, contract dates, opening a ticket), using
    // its own knowledge base. Never speaks to the caller directly.
    'support_telephony',
];
export const AGENT_REASONING_EFFORTS = ['minimal', 'low', 'medium', 'high'];
export const AGENT_PROFILE_PROVIDERS = [
    'openai',
    'anthropic',
    'deepseek',
    'gemini',
];
/**
 * Offline fallback catalog, newest first. Model dropdowns prefer the live
 * list from GET /admin/llm-models; this list is used when the provider is
 * unreachable or no key is saved. Saved profiles may use any non-empty id
 * (including ones no longer listed here). The first id is the recommended
 * fallback for that provider.
 */
export const AGENT_MODEL_CATALOG = {
    openai: [
        'gpt-6.1-sol',
        'gpt-5.6-sol',
        'gpt-5.6-terra',
        'gpt-5.6-luna',
        'gpt-4.1',
        'gpt-4.1-mini',
        'gpt-4o',
        'gpt-4o-mini',
    ],
    anthropic: [
        'claude-opus-5-5',
        'claude-sonnet-5-5',
        'claude-haiku-5-5',
        'claude-fable-5-1',
        'claude-opus-4-8',
        'claude-opus-4-7',
        'claude-sonnet-4-6',
        'claude-haiku-4-5',
    ],
    deepseek: ['deepseek-chat', 'deepseek-reasoner'],
    gemini: [
        'gemini-pro-latest',
        'gemini-flash-latest',
        'gemini-2.5-pro',
        'gemini-2.5-flash',
        'gemini-2.0-flash',
        'gemini-1.5-pro',
    ],
};
/**
 * Ordered id patterns for the "Recommended" tag. The first live model
 * (newest first) that matches the first matching pattern wins.
 */
export const MODEL_RECOMMENDED_PATTERNS = {
    openai: [/^gpt-6/, /^gpt-5/],
    anthropic: [/^claude-opus/, /^claude-sonnet/],
    gemini: [/pro-latest/, /^gemini-\d+(\.\d+)?-pro/],
    deepseek: [/^deepseek-reasoner/, /^deepseek-chat/],
};
/** Same walk as the recommended patterns, for the cheap/fast tag. */
export const MODEL_FAST_PATTERNS = {
    openai: [/mini/, /nano/],
    anthropic: [/^claude-haiku/],
    gemini: [/flash-latest/, /flash/],
    deepseek: [/^deepseek-chat/],
};
/** First id in `ids` that matches the first pattern which matches anything. */
export function pickModelByPatterns(ids, patterns) {
    for (const pattern of patterns) {
        const hit = ids.find((id) => pattern.test(id));
        if (hit)
            return hit;
    }
    return null;
}
/**
 * Recommended model for a provider. When `liveModelIds` is the cached live
 * list (newest first), the first pattern match wins. Otherwise the fallback
 * catalog's first id.
 */
export function getDefaultModelForProvider(provider, liveModelIds) {
    if (liveModelIds && liveModelIds.length > 0) {
        const picked = pickModelByPatterns(liveModelIds, MODEL_RECOMMENDED_PATTERNS[provider]);
        if (picked)
            return picked;
    }
    return AGENT_MODEL_CATALOG[provider][0];
}
// ---------------------------------------------------------------------------
// PR #D — Skills + Tool dispatcher
// ---------------------------------------------------------------------------
/** State machine values from PRD §5.2 (Curator manages transitions in PR #E). */
export const AGENT_SKILL_STATES = ['active', 'stale', 'archived'];
export const AGENT_SKILL_SOURCES = [
    'bundled',
    'admin_manual',
    'background_review',
    'learn_command',
];
/**
 * Catalog of tools the dispatcher knows how to execute. Names are short
 * dotted slugs that double as the function name the LLM sees. The schema
 * is the JSON Schema describing the tool's argument shape.
 */
/**
 * Tool name slugs. NOTE: OpenAI's function-calling API restricts function
 * names to `^[a-zA-Z0-9_-]+$` — no dots allowed. We use underscores as the
 * `namespace_verb` separator. Bootstrap auto-rewrites any pre-PR-D entries
 * that used the dotted form (`skill.read`, `web.search`).
 */
export const AGENT_TOOL_NAMES = [
    'skill_read',
    'web_search',
    // PR #E — write tools used by the background-review fork.
    'memory_write',
    'skill_create',
    // v2.4.0 PR 3 — Odoo ERP MCP tools. Only the Odoo AI Agent profile (seeded
    // in PR 4) ever whitelists these; every call is permissioned per-user ×
    // per-model at execution time (odoo-tool-common) and audited to OdooMcpLog.
    'odoo_list_models',
    'odoo_get_model_fields',
    'odoo_search_read',
    'odoo_create',
    'odoo_write',
    'odoo_unlink',
    'odoo_call_method',
    // Automation module — the Marketing Full Auto agent's tools. Each wraps an
    // existing module service (Content Calendar, Visuals Generator, Template
    // Fill, Media Calendar). Only the Automation agent profiles whitelist these.
    // `media_schedule_post` is the single mutating (Confirm-gated) tool.
    'content_generate_campaign',
    'content_get_campaign',
    'visuals_list_prompt_templates',
    'visuals_generate_image',
    'visuals_generate_video',
    'visuals_poll_generation',
    'template_list',
    'template_get_layers',
    'template_fill',
    'preview_social_post',
    'request_confirmation',
    'media_list_channels',
    'media_schedule_post',
    // UST-3132 — Telephony Support tools. Only the Telephony Support profile
    // whitelists these; each one is scoped to support data for ONE caller.
    'support_find_customer',
    'support_list_tickets',
    'support_get_ticket',
    'support_open_ticket',
    'support_add_ticket_note',
    // UST-3147 — writes a contact point the caller volunteers onto their
    // customer card. Fills blanks only; a disagreement is noted on the ticket.
    'support_save_contact',
    // UST-3159 — check a contract ID the caller reads out; set a ticket's
    // contract ID, "no contract ID" or service type.
    'support_check_contract',
    'support_set_ticket_contract',
    // Support workflow & policies — move a ticket to another workflow stage and
    // fill its ticket fields, under the stage's rules.
    'support_move_ticket',
    // Support workflow & policies — before opening a ticket: the contract type's
    // multi-ticket policy, the customer's open tickets and the Priority policy.
    'support_check_ticket_policy',
    // Support Contacts — register the caller as a support contact, or fill the
    // blanks of their record (never a duplicate, never an overwrite).
    'support_save_customer',
];
// ---------------------------------------------------------------------------
// PR #E — Memory + Curator
// ---------------------------------------------------------------------------
/** Caps enforced by memory.service.ts on every write. Match PRD §13.2. */
export const AGENT_MEMORY_MD_MAX_BYTES = 64 * 1024;
export const AGENT_USER_MD_MAX_BYTES = 32 * 1024;
export const AGENT_MEMORY_BLOCKS = ['memory', 'user'];
/** Background-review fork mode (PRD §11.1). */
export const AGENT_REVIEW_MODES = ['memory', 'skill', 'combined'];
//# sourceMappingURL=agent-profile.js.map